import GeneralDialog from '../../shared/general-dialog';
import { useMutation } from '@tanstack/react-query';
import { handleErrors } from '../../utils/errors';
import { Dispatch, SetStateAction, useState } from 'react';

import { runBulkReport, runReport } from '../../api/users-api';
import ChangeLogDialog from '../../shared/change-log-dialog';
import { useStore } from '../state/zustand-store';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { useLocation } from 'react-router-dom';
import { ModuleList } from '../../shared/analytics/module-list';
import { isAbleToSeeUIElement } from '../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface ReportsDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedReport: {
    displayName: string;
    reportType: string;
    role: string;
  } | null;
  sourceEmails: string[];
  sourceType: string;
  setSelectedUsers?: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function ReportsDialog({
  open,
  handleClose,
  selectedReport,
  sourceEmails,
  sourceType,
  setSelectedUsers,
  changeLogRoles,
}: ReportsDialogProps) {
  const location = useLocation();
  const userRoute = location.pathname.split('/')[1];
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending: isCreating } = useMutation({
    mutationFn: () =>
      runReport(
        sourceEmails[0],
        selectedReport?.reportType || '',
        sourceType,
        {
          manualRun: true,
          scheduled: false,
          reportType: selectedReport?.reportType,
          runImmediately: true,
          useReportName: false,
          autoShare: false,
          dynamicReportName: false,
          allSource: false,
          startDate: 0,
          endDate: 0,
          reportOnLastXDays: 0,
          regexTypeSelectedList: [],
          _notifyViaEmail: false,
          daysOfWeek: false,
        },
        changeLogMessage
      ),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'The generated report has opened in a new tab.',
      });

      window.open(
        `https://docs.google.com/spreadsheets/d/${data}/edit#gid=0`,
        '_blank',
        'noopener, noreferrer'
      );
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue generating a report. Please refresh and try again.',
      });
    },
    onSettled: () => {
      const eventName =
        userRoute === 'User Management'
          ? analytics.user_management.user_grid.create_report
          : userRoute.startsWith('group')
          ? analytics.directory.group_details.create_report
          : analytics.user_management.user_details.create_report;
      const eventObject =
        userRoute === 'User Management'
          ? {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.name,
              reportType: selectedReport?.reportType || '',
            }
          : userRoute.startsWith('group')
          ? {
              module: ModuleList.modules.directory.name,
              section: ModuleList.modules.directory.sections.groups,
              reportType: selectedReport?.reportType || '',
            }
          : {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.name,
              reportType: selectedReport?.reportType || '',
            };
      createEvent(eventName, eventObject);
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: bulkMutate, isPending: isBulkCreating } = useMutation({
    mutationFn: () =>
      runBulkReport(
        sourceEmails,
        selectedReport?.reportType || '',
        'User',
        {
          manualRun: true,
          scheduled: false,
          reportType: selectedReport?.reportType,
          runImmediately: true,
          useReportName: false,
          autoShare: false,
          dynamicReportName: false,
          allSource: false,
          startDate: 0,
          endDate: 0,
          reportOnLastXDays: 0,
          regexTypeSelectedList: [],
          _notifyViaEmail: false,
          daysOfWeek: false,
        },
        changeLogMessage
      ),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The generated report has opened in a new tab.',
      });

      window.open(
        `https://docs.google.com/spreadsheets/d/${data}/edit#gid=0`,
        '_blank',
        'noopener, noreferrer'
      );
      setChangeLogMessage('');

      setSelectedUsers?.([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue generating a report. Please refresh and try again.',
      });
    },
    onSettled: () => {
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, selectedReport!.role)) {
      setOpenChangeLogDialog(true);
    } else {
      if (sourceEmails.length > 1) {
        bulkMutate();
      } else {
        mutate();
      }
    }
  }

  return (
    <GeneralDialog
      title={sourceType === 'User' ? 'User Reports' : 'Group Reports'}
      open={open}
      handleClose={handleClose}
      dialogContent={
        <Box>
          {sourceEmails.length > 1 ? (
            <Typography>
              Would you like to kick off a {selectedReport?.displayName} report
              for the selected {sourceEmails.length} users?
            </Typography>
          ) : (
            <Typography>
              Would you like to kick off a {selectedReport?.displayName} report
              for {sourceEmails[0]}?
            </Typography>
          )}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              if (sourceEmails.length > 1) {
                bulkMutate();
              } else {
                mutate();
              }
            }}
            mutateLoading={isCreating || isBulkCreating}
          />
        </Box>
      }
      primaryButton={{
        title: 'Run',
        action: handleSubmit,
        loading: isCreating || isBulkCreating,
        disabled: isCreating || isBulkCreating,
      }}
    />
  );
}
